import { useState } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { CodeBuilderConfigPanel } from './CodeBuilderConfigPanel';
import { CodeBuilderInterviewPanel } from './CodeBuilderInterviewPanel';
import { CodeBuilderDevModePanel } from './CodeBuilderDevModePanel';
import type { CodeBuilderFormData, CodeBuilderUiOptions } from './types';

// Two views of the same work: the Interview drives it, Dev Mode is a read-only
// IDE view of the draft files the interview has produced. The repo/issue
// pickers stay visible in both; the toggle sits just above the view area.
type WorkspaceMode = 'interview' | 'devMode';

export const CodeBuilderField = (
  props: FieldExtensionComponentProps<CodeBuilderFormData, CodeBuilderUiOptions>,
) => {
  const uiOptions =
    (props.uiSchema?.['ui:options'] as CodeBuilderUiOptions | undefined) ?? {};
  const formData = props.formData ?? {};

  const [mode, setMode] = useState<WorkspaceMode>('interview');

  const onChange = (patch: Partial<CodeBuilderFormData>) => {
    props.onChange({ ...formData, ...patch });
  };

  const toggleBar = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 6,
        padding: '6px 8px',
        borderTop: '1px solid rgba(128,128,128,0.3)',
        borderBottom: '1px solid rgba(128,128,128,0.3)',
      }}
    >
      <Typography style={{ fontSize: 12, color: 'inherit' }}>
        {mode === 'devMode' ? 'Interview' : 'Dev Mode'}
      </Typography>
      <Switch
        size="small"
        color="primary"
        checked={mode === 'devMode'}
        onChange={() =>
          setMode(current => (current === 'interview' ? 'devMode' : 'interview'))
        }
        inputProps={{ 'aria-label': 'Toggle Dev Mode' }}
      />
    </div>
  );

  return (
    <div
      style={{
        border: '1px solid rgba(128,128,128,0.3)',
        borderRadius: 12,
        minHeight: uiOptions.height ?? 480,
        height: uiOptions.height ?? 480,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <CodeBuilderConfigPanel
        formData={formData}
        uiOptions={uiOptions}
        onChange={onChange}
      />
      {toggleBar}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {mode === 'devMode' ? (
          <CodeBuilderDevModePanel draft={formData.draft} />
        ) : (
          <CodeBuilderInterviewPanel formData={formData} onChange={onChange} />
        )}
      </div>
    </div>
  );
};
